import React, { useEffect, useState } from 'react';
import { usePower } from '../state/PowerContext';
import { getHiddenPeakRisk } from '../api';
import { weatherConfidence, daysBetween } from '../utils/confidence';
import { todayISO } from '../utils/billingPeriod';

function hourLabel(h) {
  const hh = ((h - 1) % 12) + 1;
  const ampm = h >= 12 && h < 24 ? 'PM' : 'AM';
  return `${hh}${ampm}`;
}

function fmtDate(iso) {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function fmt(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

// Weather-extended peak-risk: for each upcoming forecast day, compare its
// forecast peak temperature against historical hours near that temperature
// and look at the NEXT-hour power (usage lags temp by ~1 hr). Flags days
// where history suggests a peak exceeding the current period high — the
// "hidden" peaks that live beyond MISO's 5-day forecast horizon.
// Show the period peak + the 3 hours leading up to it (value, temp, LMP),
// so the historical table below has a concrete baseline to compare against.
function PeakContext({ records, periodHigh }) {
  if (!periodHigh) return null;

  function prevDate(iso) {
    const d = new Date(iso + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }
  function nextDate(iso) {
    const d = new Date(iso + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }

  const byKey = new Map((records || []).map(r => [`${r.date}_${r.hour}`, r]));

  // Collect the 4 cells: peak-3h, peak-2h, peak-1h, peak. Walk the hour clock,
  // rolling across midnight if needed.
  const cells = [];
  for (let offset = -3; offset <= 0; offset++) {
    let date = periodHigh.date;
    let hour = periodHigh.hour + offset;
    while (hour < 1)  { hour += 24; date = prevDate(date); }
    while (hour > 24) { hour -= 24; date = nextDate(date); }
    cells.push({
      date, hour,
      isPeak: offset === 0,
      rec: byKey.get(`${date}_${hour}`) || null,
    });
  }

  return (
    <div className="peak-context">
      <div className="peak-ctx-label">
        Period peak so far · the 3 hours leading up to it
      </div>
      <div className="peak-ctx-strip">
        {cells.map((c, i) => (
          <div key={i} className={`peak-ctx-cell ${c.isPeak ? 'peak' : ''}`}>
            <div className="when">{fmtDate(c.date)} · {hourLabel(c.hour)}</div>
            <div className="val">{c.rec?.value != null ? fmt(c.rec.value) : '—'}</div>
            <div className="meta">
              {c.rec?.temperature != null && <span className="temp">{Math.round(c.rec.temperature)}°F</span>}
              {c.rec?.lmp != null && <span className="lmp">${c.rec.lmp.toFixed(c.rec.lmp >= 100 ? 0 : 1)}</span>}
              {c.rec?.isForecast !== undefined && (
                <span className="src">{c.rec.isForecast ? 'forecast' : 'actual'}</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {periodHigh.isForecast === false && (
        <div className="peak-ctx-hint">
          The peak above is a settled actual. If weather forecasts push any upcoming day's peak temperature above this temp, the table below is where that day shows up.
        </div>
      )}
    </div>
  );
}

export default function HiddenPeakRisk({ periodHigh, records, hiddenRisk }) {
  const { series } = usePower();
  // hiddenRisk is now passed in from Dashboard so we share one fetch.
  // Fall back to local fetch only if a parent forgot to pass it.
  const [data, setData] = useState(hiddenRisk ? { results: hiddenRisk, tolerance_f: 2.5 } : null);
  const [loading, setLoading] = useState(!hiddenRisk);

  useEffect(() => {
    if (hiddenRisk) {
      setData({ results: hiddenRisk, tolerance_f: 2.5 });
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getHiddenPeakRisk(series, 15)
      .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch(() => { if (!cancelled) { setData(null); setLoading(false); } });
    return () => { cancelled = true; };
  }, [series, hiddenRisk]);

  if (loading) {
    return (
      <div className="panel">
        <h2>Hidden peak risk (weather-inferred)</h2>
        <PeakContext records={records} periodHigh={periodHigh} />
        <div className="loading" style={{ padding: 16 }}>Analyzing…</div>
      </div>
    );
  }

  const results = data?.results || [];
  if (!results.length) {
    return (
      <div className="panel">
        <h2>Hidden peak risk (weather-inferred)</h2>
        <PeakContext records={records} periodHigh={periodHigh} />
        <div style={{ color: 'var(--text-dim)' }}>No weather forecast data available yet, or no historical matches found.</div>
      </div>
    );
  }

  const highVal = periodHigh?.value ?? null;

  return (
    <div className="panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Hidden peak risk · weather-inferred · next 15 days · {series}</h2>
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>
          each day: forecast peak temp + historical next-hour power distribution
        </span>
      </div>
      <PeakContext records={records} periodHigh={periodHigh} />
      <table className="hist-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Days out</th>
            <th>Conf.</th>
            <th>Peak temp</th>
            <th>@ hour</th>
            <th className="num" title="90th percentile — 9 of 10 similar historical days stayed below this">
              Hist. P90
            </th>
            <th className="num" title="weighted = P90 × confidence">Weighted</th>
            <th className="num" title="worst single day ever recorded with this peak temp (one-off outlier, included for context)">
              Max ever
            </th>
            <th className="num">n</th>
            <th>Risk vs period high</th>
          </tr>
        </thead>
        <tbody>
          {results.map(r => {
            const daysOut = daysBetween(todayISO(), r.future_date);
            const conf = weatherConfidence(daysOut);
            // Use P90 as the "what to expect" basis. Max is the worst single
            // outlier across years and biases risk calls toward false alarms.
            const baseline = r.historical_p90_power;
            const weighted = baseline * conf;
            const hitPct = highVal ? weighted / highVal : null;
            let risk = 'clear';
            let risk_label = 'below period high';
            if (hitPct != null) {
              if (hitPct >= 1.0)       { risk = 'danger';  risk_label = `≥${Math.round(hitPct*100)}% of period high — likely to break it`; }
              else if (hitPct >= 0.95) { risk = 'danger';  risk_label = `${Math.round(hitPct*100)}% — within 5% of period high`; }
              else if (hitPct >= 0.90) { risk = 'warning'; risk_label = `${Math.round(hitPct*100)}% — within 10%`; }
              else                     { risk = 'clear';   risk_label = `${Math.round(hitPct*100)}% of period high`; }
            }
            return (
              <tr key={r.future_date}>
                <td>{fmtDate(r.future_date)}</td>
                <td>{daysOut}d</td>
                <td><span className="conf-pill">{Math.round(conf * 100)}%</span></td>
                <td>{r.future_peak_temp_f}°F</td>
                <td>{hourLabel(r.future_peak_hour)}</td>
                <td className="num">{fmt(r.historical_p90_power)}</td>
                <td className="num"><strong>{fmt(weighted)}</strong></td>
                <td className="num" style={{ color: 'var(--text-dim)', fontSize: 12 }}>{fmt(r.historical_max_power)}</td>
                <td className="num">{r.historical_sample_count}</td>
                <td>
                  <span className={`badge ${risk}`} style={{ fontSize: 10 }}>{risk.toUpperCase()}</span>{' '}
                  <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{risk_label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
        Matched against historical days whose peak temp was within ±{data.tolerance_f}°F of this day's forecast peak, in the same time of year (±30 days).
        "Hist. P90" = the 90th-percentile daily peak power across those matches — 9 of 10 similar days stayed at or below this. That's the realistic upper bound used for risk decisions.
        "Weighted" = P90 × confidence (decays farther out: ~100% at 6 days, 80% at 9, 60% at 12, 45% at 14+). "Max ever" is the single worst day ever recorded in the bucket — included for context only, not used in risk classification because it can be a one-off outlier.
      </div>
    </div>
  );
}
